import React, { useState, useRef, useLayoutEffect, useCallback } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Tooltip } from "@/components/common/Tooltip";
import { DropdownMenu } from "components/common/DropdownMenu";
import { DropdownMenuItem } from "components/common/DropdownMenuItem";
import { PersonOption } from "components/common/PersonPicker";
import { validateEmail, ResourceType } from "./entities";
import { axiosInstance } from "contexts/axiosApiInstance";

export interface EmailChip {
  email: string;
  status: "valid" | "invalid" | "duplicate" | "validating";
  helper?: string;
}

// Deliberately answers about the RESOURCE, never the address — there is no
// "has an account" / "will be invited" split to render, because rendering one
// made this an account-existence oracle. The invited-vs-granted distinction
// belongs to the server alone.
interface EmailValidationResponse {
  email: string;
  status: "shareable" | "already_covered" | "not_in_org" | "invalid_format";
}

export interface DirectoryEntry {
  email: string;
  displayName?: string;
}

interface EmailChipInputProps {
  chips: EmailChip[];
  onChipsChange: (chips: EmailChip[]) => void;
  entityId?: string;
  resourceType?: ResourceType;
  currentUserEmail?: string;
  placeholder?: string;
  ariaLabel?: string;
  error?: string | null;
  onErrorChange?: (error: string | null) => void;
  /// A company org's roster: when set, entry is restricted to it — typing
  /// filters these as suggestions, and an address outside the list is marked
  /// invalid immediately instead of asking the server about it.
  directory?: DirectoryEntry[];
  /// Names the boundary in the invalid-chip helper ("Not a member of Acme").
  directoryLabel?: string;
  /// Who already holds the resource — the client-side answer for doors with no
  /// `validate_emails` arm. Re-granting one would write a second grant.
  alreadyGranted?: string[];
}

const tokenize = (value: string) =>
  value
    .split(/[\s,]+/)
    .map((email) => email.trim())
    .filter((email) => email.length > 0);

const validateEmailChipLocal = (
  email: string,
  existingChips: Set<string>,
  currentUserEmail?: string,
): {
  status: "valid" | "invalid" | "duplicate" | "validating";
  helper?: string;
} => {
  const trimmed = email.trim();
  const lower = trimmed.toLowerCase();
  const alreadyAdded = existingChips.has(lower);
  const isValid = validateEmail(trimmed);

  if (!isValid) {
    return { status: "invalid", helper: "Invalid email address" };
  }

  if (currentUserEmail && lower === currentUserEmail.trim().toLowerCase()) {
    return { status: "duplicate", helper: "You can't add yourself" };
  }

  if (alreadyAdded) {
    return { status: "duplicate", helper: "Email already added" };
  }

  return { status: "validating", helper: "Validating..." };
};

const validateEmailsApi = async (
  emails: string[],
  entityId: string,
  resourceType: ResourceType,
): Promise<Map<string, EmailChip>> => {
  const resource =
    resourceType === ResourceType.Model
      ? { model: entityId }
      : resourceType === ResourceType.Dataset
        ? { dataset: entityId }
        : { table: entityId };

  const response = await axiosInstance.post<EmailValidationResponse[]>(
    "/action/validate_emails",
    { emails, resource },
  );

  const resultMap = new Map<string, EmailChip>();
  for (const result of response.data) {
    const emailLower = result.email.toLowerCase();
    switch (result.status) {
      case "shareable":
        resultMap.set(emailLower, { email: result.email, status: "valid" });
        break;
      case "already_covered":
        resultMap.set(emailLower, {
          email: result.email,
          status: "duplicate",
          helper: "Already has access or a pending invite",
        });
        break;
      case "not_in_org":
        resultMap.set(emailLower, {
          email: result.email,
          status: "invalid",
          helper: "Not a member of this organization",
        });
        break;
      case "invalid_format":
        resultMap.set(emailLower, {
          email: result.email,
          status: "invalid",
          helper: "Invalid email address",
        });
        break;
    }
  }
  return resultMap;
};

export const EmailChipInput: React.FC<EmailChipInputProps> = ({
  chips,
  onChipsChange,
  entityId,
  resourceType,
  currentUserEmail,
  placeholder = "Emails, comma separated",
  ariaLabel,
  error,
  onErrorChange,
  directory,
  directoryLabel,
  alreadyGranted,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");
  const [inputWidth, setInputWidth] = useState<number | null>(null);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);
  const chipRefs = useRef<{ [key: number]: HTMLSpanElement | null }>({});
  const measureRef = useRef<HTMLSpanElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const grantedLower = React.useMemo(
    () => new Set((alreadyGranted ?? []).map((e) => e.toLowerCase())),
    [alreadyGranted],
  );

  // Roster suggestions: match on name or address, hide what's already a chip
  // and anyone the resource already reaches.
  const query = inputValue.trim().toLowerCase();
  const suggestions = React.useMemo(() => {
    if (!directory || query.length === 0) return [];
    const taken = new Set(chips.map((c) => c.email.toLowerCase()));
    return directory
      .filter(
        (m) =>
          !taken.has(m.email.toLowerCase()) &&
          !grantedLower.has(m.email.toLowerCase()) &&
          m.email.toLowerCase() !== currentUserEmail?.trim().toLowerCase() &&
          (m.email.toLowerCase().includes(query) ||
            m.displayName?.toLowerCase().includes(query)),
      )
      .slice(0, 6);
  }, [directory, query, chips, currentUserEmail, grantedLower]);
  const suggestionsOpen = suggestions.length > 0 && !suggestionsDismissed;

  /// What the client can settle without the server: who already holds it, and
  /// — where a directory is set — that it is the universe entry may come from.
  const screenChip = useCallback(
    (chip: EmailChip): EmailChip => {
      if (chip.status === "invalid" || chip.status === "duplicate") return chip;
      const lower = chip.email.toLowerCase();
      if (grantedLower.has(lower)) {
        return {
          email: chip.email,
          status: "duplicate",
          helper: "Already has access",
        };
      }
      if (!directory) return chip;
      const known = directory.some((m) => m.email.toLowerCase() === lower);
      return known
        ? chip
        : {
            email: chip.email,
            status: "invalid",
            helper: `Not a member of ${directoryLabel ?? "this organization"}`,
          };
    },
    [directory, directoryLabel, grantedLower],
  );

  const validateChipsWithApi = useCallback(
    async (chipsToValidate: EmailChip[], allChips: EmailChip[]) => {
      const emailsToValidate = chipsToValidate
        .filter((chip) => chip.status === "validating")
        .map((chip) => chip.email);

      if (emailsToValidate.length === 0) return;

      if (!entityId || !resourceType) {
        const emailSet = new Set(emailsToValidate.map((e) => e.toLowerCase()));
        onChipsChange(
          allChips.map((chip) =>
            emailSet.has(chip.email.toLowerCase()) &&
            chip.status === "validating"
              ? { email: chip.email, status: "valid" }
              : chip,
          ),
        );
        return;
      }

      try {
        const resultMap = await validateEmailsApi(
          emailsToValidate,
          entityId,
          resourceType,
        );

        const updatedChips = allChips.map((chip) => {
          const result = resultMap.get(chip.email.toLowerCase());
          if (result) {
            return result;
          }
          return chip;
        });

        onChipsChange(updatedChips);
      } catch {
        const updatedChips = allChips.map((chip) =>
          chip.status === "validating"
            ? { ...chip, status: "valid" as const }
            : chip,
        );
        onChipsChange(updatedChips);
      }
    },
    [entityId, resourceType, onChipsChange],
  );

  const handleChipAdd = (rawInput: string) => {
    const nextTokens = tokenize(rawInput);
    if (nextTokens.length === 0) return;

    const existingLower = new Set(
      chips.map((chip) => chip.email.toLowerCase()),
    );

    const newChips = nextTokens.map((email) => {
      const trimmed = email.trim();
      const validation = validateEmailChipLocal(
        trimmed,
        existingLower,
        currentUserEmail,
      );

      if (
        validation.status !== "invalid" &&
        validation.status !== "duplicate"
      ) {
        existingLower.add(trimmed.toLowerCase());
      }
      return screenChip({ email: trimmed, ...validation });
    });

    const allChips = [...chips, ...newChips];
    onChipsChange(allChips);
    setInputValue("");
    onErrorChange?.(null);

    validateChipsWithApi(newChips, allChips);
  };

  const pickSuggestion = (email: string) => {
    handleChipAdd(email);
    setHighlightIndex(0);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (suggestionsOpen) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightIndex((i) => (i + 1) % suggestions.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightIndex(
          (i) => (i - 1 + suggestions.length) % suggestions.length,
        );
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        pickSuggestion(
          suggestions[Math.min(highlightIndex, suggestions.length - 1)].email,
        );
        return;
      }
    }
    if (
      event.key === "Enter" ||
      event.key === "," ||
      (event.key === "Tab" && inputValue.trim())
    ) {
      event.preventDefault();
      handleChipAdd(inputValue);
      return;
    }
    if (event.key === "Backspace" && inputValue.length === 0 && chips.length) {
      event.preventDefault();
      onChipsChange(chips.slice(0, -1));
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData("text");
    if (text && /[,|\s]/.test(text)) {
      event.preventDefault();
      handleChipAdd(text);
    }
  };

  const handleChipEdit = (index: number) => {
    const chipElement = chipRefs.current[index];
    if (chipElement) {
      const width = chipElement.offsetWidth;
      setInputWidth(width);
    }
    setEditingIndex(index);
    setEditingValue(chips[index].email);
  };

  useLayoutEffect(() => {
    if (editingIndex !== null && measureRef.current) {
      const width = measureRef.current.offsetWidth;
      setInputWidth(width + 4);
    }
  }, [editingValue, editingIndex]);

  const handleChipEditComplete = (index: number) => {
    if (editingIndex === null) return;

    const trimmed = editingValue.trim();
    if (trimmed === "") {
      onChipsChange(chips.filter((_, idx) => idx !== index));
    } else {
      const existingEmails = new Set(
        chips
          .filter((_, idx) => idx !== index)
          .map((chip) => chip.email.toLowerCase()),
      );
      const validation = validateEmailChipLocal(
        trimmed,
        existingEmails,
        currentUserEmail,
      );

      const editedChip = screenChip({ email: trimmed, ...validation });
      const newChips = chips.map((chip, idx) =>
        idx === index ? editedChip : chip,
      );
      onChipsChange(newChips);

      if (editedChip.status === "validating") {
        validateChipsWithApi([editedChip], newChips);
      }
    }

    setEditingIndex(null);
    setEditingValue("");
    setInputWidth(null);
  };

  const handleChipEditKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleChipEditComplete(index);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setEditingIndex(null);
      setEditingValue("");
      setInputWidth(null);
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full input-custom-sm flex flex-wrap items-center gap-1 h-auto min-h-8 px-2 py-1 focus-within:border-accent"
    >
      {editingIndex !== null && (
        <span
          ref={measureRef}
          className="absolute invisible text-xs font-medium px-2"
          style={{
            whiteSpace: "pre",
            fontFamily: "inherit",
            visibility: "hidden",
            position: "absolute",
            top: "-9999px",
          }}
        >
          {editingValue || " "}
        </span>
      )}

      {chips.map((chip, index) => (
        <React.Fragment key={`${chip.email}-${index}`}>
          {editingIndex === index ? (
            <input
              type="text"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={() => handleChipEditComplete(index)}
              onKeyDown={(e) => handleChipEditKeyDown(e, index)}
              autoFocus
              style={{
                width: inputWidth ? `${inputWidth}px` : "auto",
              }}
              className="inline-flex items-center gap-1 rounded-app border px-2 py-1 text-xs font-medium bg-bg-hover text-fg-medium outline-none"
            />
          ) : (
            <Tooltip
              title={chip.helper || ""}
              placement="top"
              disableHoverListener={
                chip.status === "valid" || chip.status === "validating"
              }
            >
              <span
                ref={(el) => {
                  chipRefs.current[index] = el;
                }}
                onDoubleClick={() => handleChipEdit(index)}
                className={`inline-flex items-center gap-1 rounded-app border px-2 py-1 text-xs font-medium cursor-text ${
                  chip.status === "valid"
                    ? "border-(--border-color) bg-transparent text-fg-medium hover:bg-border"
                    : chip.status === "validating"
                      ? "border-(--border-color) bg-transparent text-fg-muted-medium"
                      : "border-error bg-transparent text-error hover:bg-error/10"
                }`}
              >
                {chip.email}
                <button
                  type="button"
                  className="text-current hover:opacity-80 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChipsChange(chips.filter((_, idx) => idx !== index));
                  }}
                >
                  <CloseIcon
                    style={{
                      fontSize: "10px",
                      width: "10px",
                      height: "10px",
                    }}
                  />
                </button>
              </span>
            </Tooltip>
          )}
        </React.Fragment>
      ))}

      <input
        value={inputValue}
        onChange={(event) => {
          setInputValue(event.target.value);
          setSuggestionsDismissed(false);
          setHighlightIndex(0);
          if (error) onErrorChange?.(null);
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => inputValue && handleChipAdd(inputValue)}
        onPaste={handlePaste}
        placeholder={chips.length === 0 ? placeholder : ""}
        aria-label={ariaLabel}
        // The browser's address autofill covers the roster list with a menu the
        // app does not control and cannot style.
        autoComplete="off"
        className="flex-1 min-w-20 border-none bg-transparent outline-none"
      />
      <DropdownMenu
        anchorRef={containerRef}
        isOpen={suggestionsOpen}
        onClose={() => setSuggestionsDismissed(true)}
      >
        {/* Keeping focus in the input is what makes a click on a suggestion
            land at all: blurring commits the half-typed text, which empties
            the query, which unmounts this menu before the click arrives — so
            picking a name silently added the stub instead. */}
        <div onMouseDown={(event) => event.preventDefault()}>
          {suggestions.map((member, index) => (
            <DropdownMenuItem
              key={member.email}
              isSelected={index === highlightIndex}
              onClick={() => pickSuggestion(member.email)}
            >
              <PersonOption
                email={member.email}
                displayName={member.displayName}
              />
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenu>
    </div>
  );
};
